"use client";

import React, { useState } from "react";
import { Filter, ArrowRight, CircleCheck, Gauge, Zap, ShieldCheck, SlidersHorizontal, FileText } from "lucide-react";
import { Link } from "@/components/common/Link";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Card, CardHeader, CardTitle, CardDescription, CardContent } from "@/components/ui/card";
import { Table, TableHeader, TableBody, TableRow, TableHead, TableCell } from "@/components/ui/table";
import Reveal from "@/components/common/Reveal";
import { useLanguage } from "@/hooks/useLanguage";
import { pick } from "@/lib/i18n";
interface MediaSpec {
  id: string;
  name: string;
  category: string;
  micronRating: string;
  flowResistance: string;
  rigidity: string;
  cleanability: string;
  primaryApplication: string;
}
export default function FilterMedia() {
  const {
    lang
  } = useLanguage();
  const [selectedMediaId, setSelectedMediaId] = useState<string>("pleated-sintered");
  const mediaSpecs: MediaSpec[] = [{
    id: "pleated-sintered",
    name: pick(lang, {
      en: "Pleated Sintered Wire Mesh",
      zh: "折叠烧结金属网滤芯",
      es: "Malla de alambre sinterizada plisada",
      de: "Plissiertes Sinterdrahtgewebe",
      fr: "Toile métallique frittée plissée",
      pt: "Malha de arame sinterizada pregueada"
    }),
    category: pick(lang, {
      en: "High-Surface Filter",
      zh: "高比表面积过滤",
      es: "Filtro de alta superficie",
      de: "Hochflächenfilter",
      fr: "Filtre à haute surface",
      pt: "Filtro de alta superfície"
    }),
    micronRating: "1 µm – 200 µm",
    flowResistance: pick(lang, {
      en: "Low ΔP (Expanded Area)",
      zh: "低压差 (展开面积大)",
      es: "Bajo ΔP (área expandida)",
      de: "Niedriger ΔP (vergrößerte Fläche)",
      fr: "Faible ΔP (surface étendue)",
      pt: "Baixo ΔP (área expandida)"
    }),
    rigidity: pick(lang, {
      en: "High (316L Matrix)",
      zh: "高刚性 (316L 基体)",
      es: "Alta (matriz 316L)",
      de: "Hoch (316L Matrix)",
      fr: "Élevée (matrice 316L)",
      pt: "Alta (matriz 316L)"
    }),
    cleanability: pick(lang, {
      en: "Ultrasonic / Backwash",
      zh: "超声波 / 反冲洗",
      es: "Ultrasónica / retrolavado",
      de: "Ultraschall / Rückspülung",
      fr: "Ultrasons / rétrolavage",
      pt: "Ultrassônica / retrolavagem"
    }),
    primaryApplication: pick(lang, {
      en: "Polymer melt, high-viscosity liquid & hot gas filtration",
      zh: "聚合物熔体、高粘度流体及高温气体过滤",
      es: "Polímeros fundidos, líquidos de alta viscosidad y gases calientes",
      de: "Polymerschmelze, hochviskose Flüssigkeiten & Heißgasfiltration",
      fr: "Fusion de polymères, liquides haute viscosité et gaz chauds",
      pt: "Polímeros fundidos, líquidos de alta viscosidade e gases quentes"
    })
  }, {
    id: "sintered-multilayer",
    name: pick(lang, {
      en: "5-Layer Sintered Wire Cloth",
      zh: "五层烧结金属滤网",
      es: "Malla de alambre sinterizada de 5 capas",
      de: "5-lagiges Sinterdrahtgewebe",
      fr: "Toile métallique frittée 5 couches",
      pt: "Malha de arame sinterizada de 5 camadas"
    }),
    category: pick(lang, {
      en: "Rigid Laminate",
      zh: "刚性多孔复合板",
      es: "Laminado rígido",
      de: "Starres Laminat",
      fr: "Stratifié rigide",
      pt: "Laminado rígido"
    }),
    micronRating: "2 µm – 100 µm",
    flowResistance: pick(lang, {
      en: "Moderate ΔP",
      zh: "中等压差",
      es: "ΔP moderado",
      de: "Moderater ΔP",
      fr: "ΔP modéré",
      pt: "ΔP moderado"
    }),
    rigidity: pick(lang, {
      en: "Extremely High",
      zh: "极高机械强度",
      es: "Extremadamente alta",
      de: "Sehr hoch",
      fr: "Extrêmement élevée",
      pt: "Extremamente alta"
    }),
    cleanability: pick(lang, {
      en: "Chemical / Thermal clean",
      zh: "化学溶剂 / 高温热清洗",
      es: "Limpieza química / térmica",
      de: "Chemische / thermische Reinigung",
      fr: "Nettoyage chimique / thermique",
      pt: "Limpeza química / térmica"
    }),
    primaryApplication: pick(lang, {
      en: "Fluidized beds, chemical reactors, high-pressure strainers",
      zh: "流化床分布板、化学反应器、高压管路过滤器",
      es: "Lechos fluidizados, reactores químicos y filtros de alta presión",
      de: "Wirbelschichten, chemische Reaktoren, Hochdrucksiebe",
      fr: "Lits fluidisés, réacteurs chimiques, filtres haute pression",
      pt: "Leitos fluidizados, reatores químicos e filtros de alta pressão"
    })
  }, {
    id: "dutch-weave",
    name: pick(lang, {
      en: "Twill Dutch Weave Mesh",
      zh: "斜纹荷兰编织网",
      es: "Malla de tejido Dutch cruzado",
      de: "Köpertressengewebe",
      fr: "Toile tissage hollandais sergé",
      pt: "Malha de trama holandesa sarja"
    }),
    category: pick(lang, {
      en: "Woven Filter Cloth",
      zh: "精密编织滤布",
      es: "Tela filtrante tejida",
      de: "Webfiltertuch",
      fr: "Toile filtrante tissée",
      pt: "Tecido filtrante tecido"
    }),
    micronRating: "5 µm – 250 µm",
    flowResistance: pick(lang, {
      en: "Moderate ΔP",
      zh: "中等压差",
      es: "ΔP moderado",
      de: "Moderater ΔP",
      fr: "ΔP modéré",
      pt: "ΔP moderado"
    }),
    rigidity: pick(lang, {
      en: "Medium-High",
      zh: "中高机械挺度",
      es: "Media-alta",
      de: "Mittel bis hoch",
      fr: "Moyenne à élevée",
      pt: "Média-alta"
    }),
    cleanability: pick(lang, {
      en: "Backwashable",
      zh: "可高压反冲洗",
      es: "Retrolavable",
      de: "Rückspülbar",
      fr: "Rétrolavable",
      pt: "Retrolavável"
    }),
    primaryApplication: pick(lang, {
      en: "Fuel separation, hydraulic fluid, aerospace oil filters",
      zh: "燃油油水分离、液压油循环、航空燃油精滤",
      es: "Separación de combustible, fluidos hidráulicos y filtros aeroespaciales",
      de: "Kraftstoffabscheidung, Hydraulikflüssigkeiten, Luftfahrtölfilter",
      fr: "Séparation de carburant, fluides hydrauliques et filtres aérospatiaux",
      pt: "Separação de combustível, fluidos hidráulicos e filtros aeroespaciais"
    })
  }, {
    id: "plain-square",
    name: pick(lang, {
      en: "Square Plain Weave Mesh",
      zh: "方孔平纹编织网",
      es: "Malla tejida lisa cuadrada",
      de: "Quadratmaschengewebe (Leinwand)",
      fr: "Toile métallique unie à mailles carrées",
      pt: "Malha tecida lisa quadrada"
    }),
    category: pick(lang, {
      en: "Standard Woven Wire",
      zh: "通用工业编织网",
      es: "Alambre tejido estándar",
      de: "Standard-Drahtgewebe",
      fr: "Toile métallique standard",
      pt: "Arame tecido padrão"
    }),
    micronRating: "20 µm – 2000 µm",
    flowResistance: pick(lang, {
      en: "Very Low ΔP",
      zh: "极低流通压差",
      es: "Muy bajo ΔP",
      de: "Sehr niedriger ΔP",
      fr: "Très faible ΔP",
      pt: "Muito baixo ΔP"
    }),
    rigidity: pick(lang, {
      en: "Flexible to Medium",
      zh: "柔性至中等刚度",
      es: "Flexible a media",
      de: "Flexibel bis mittel",
      fr: "Flexible à moyenne",
      pt: "Flexível a média"
    }),
    cleanability: pick(lang, {
      en: "Manual / Flush",
      zh: "人工清洗 / 冲洗",
      es: "Manual / lavado directo",
      de: "Manuell / Durchspülen",
      fr: "Manuel / rinçage",
      pt: "Manual / lavagem direta"
    }),
    primaryApplication: pick(lang, {
      en: "Coarse screening, particle classification, sifting screens",
      zh: "粗效初滤、固相颗粒筛分级、粉体筛分",
      es: "Tamizado grueso, clasificación de partículas y cribado",
      de: "Grobsiebung, Partikelklassierung, Siebverfahren",
      fr: "Tamisage grossier, classification de particules, criblage",
      pt: "Peneiramento grosso, classificação de partículas e peneiras"
    })
  }, {
    id: "wedge-wire",
    name: pick(lang, {
      en: "Heavy-Duty Wedge Wire",
      zh: "重载楔形绕丝滤网",
      es: "Alambre en cuña para servicio pesado",
      de: "Hochleistungs-Spaltsieb",
      fr: "Fil profilé haute résistance",
      pt: "Tela de arame em cunha para serviço pesado"
    }),
    category: pick(lang, {
      en: "Slot Screen",
      zh: "缝隙滤筒/筛板",
      es: "Tamiz de ranura",
      de: "Spaltsieb",
      fr: "Tamis à fente",
      pt: "Tela de fenda"
    }),
    micronRating: "20 µm – 3000 µm",
    flowResistance: pick(lang, {
      en: "Low ΔP",
      zh: "低流动阻力",
      es: "Bajo ΔP",
      de: "Niedriger ΔP",
      fr: "Faible ΔP",
      pt: "Baixo ΔP"
    }),
    rigidity: pick(lang, {
      en: "Maximum Mechanical",
      zh: "高机械结构强度",
      es: "Máxima resistencia mecánica",
      de: "Maximale mechanische Festigkeit",
      fr: "Résistance mécanique maximale",
      pt: "Máxima resistência mecânica"
    }),
    cleanability: pick(lang, {
      en: "Continuous Scraper",
      zh: "连续刮刀机械自清洗",
      es: "Raspador continuo",
      de: "Kontinuierlicher Abstreifer",
      fr: "Racleur continu",
      pt: "Raspador contínuo"
    }),
    primaryApplication: pick(lang, {
      en: "Water intake, mineral processing, sugar & starch dewatering",
      zh: "取水口粗滤、矿物洗选、淀粉制糖固液脱水",
      es: "Toma de agua, procesamiento de minerales y deshidratación de azúcar y almidón",
      de: "Wasserentnahme, Mineralaufbereitung, Zucker- & Stärkeentwässerung",
      fr: "Prise d'eau, traitement minéral, déshydratation de sucre et d'amidon",
      pt: "Captação de água, processamento mineral e desidratação de açúcar e amido"
    })
  }];
  const selectedMedia = mediaSpecs.find(m => m.id === selectedMediaId) || mediaSpecs[0];
  const copy = {
    badge: pick(lang, {
      en: "Filter Media Performance & Selection",
      zh: "滤材性能评估与选型",
      es: "Rendimiento y selección de medios filtrantes",
      de: "Filtermedien-Leistung & Auswahl",
      fr: "Performance et sélection des médias filtrants",
      pt: "Desempenho e seleção de meios filtrantes"
    }),
    h2: pick(lang, {
      en: "Engineering Filter Media Specifications",
      zh: "工程级工业过滤介质技术规格",
      es: "Especificaciones técnicas de medios filtrantes",
      de: "Technische Spezifikationen für Filtermedien",
      fr: "Spécifications techniques des médias filtrants",
      pt: "Especificações técnicas de meios filtrantes"
    }),
    lead: pick(lang, {
      en: "MutualFilter supplies precision-engineered stainless steel woven wire cloth, sintered multilayer laminates, and pleated element structures designed to maximize open area, dirt-holding capacity, and differential pressure threshold in critical industrial processes.",
      zh: "MutualFilter 供应精密不锈钢编织丝网、多层金属烧结复合板及折叠滤芯元件，优化开孔率、纳污容量与抗差压强度，满足严苛工业流体过滤需求。",
      es: "MutualFilter suministra tela metálica tejida de acero inoxidable, laminados sinterizados multicapa y estructuras plisadas diseñadas para maximizar el área abierta y la retención de suciedad en procesos industriales críticos.",
      de: "MutualFilter liefert präzisionsgefertigtes Edelstahl-Drahtgewebe, mehrlagige Sinterlaminate und plissierte Elementstrukturen zur Optimierung von Siebfläche und Schmutzaufnahmekapazität.",
      fr: "MutualFilter fournit des toiles tissées en acier inoxydable de précision, des laminés multicouches frittés et des éléments plissés conçus pour optimiser la surface ouverte et la capacité de rétention.",
      pt: "A MutualFilter fornece telas tecidas de aço inoxidável de precisão, laminados sinterizados multicamadas e elementos pregueados projetados para maximizar a área aberta e a retenção de sólidos."
    }),
    card1Title: pick(lang, {
      en: "3x–5x Surface Area Expansion",
      zh: "3至5倍有效过滤面积展开",
      es: "Expansión de superficie de 3x a 5x",
      de: "3x–5x Oberflächenvergrößerung",
      fr: "Expansion de surface de 3x à 5x",
      pt: "Expansão de área superficial de 3x a 5x"
    }),
    card1Desc: pick(lang, {
      en: "Pleating woven or sintered mesh increases effective filtration area up to 500% within identical outer housing dimensions, significantly extending cycle life.",
      zh: "将编织网或烧结网折叠加工，可在相同外壳尺寸内提升有效过滤面积达 500%，显著延长在线运行周期。",
      es: "Plisar la malla tejida o sinterizada aumenta el área efectiva de filtración hasta un 500% en las mismas dimensiones, prolongando el ciclo de vida.",
      de: "Durch das Plissieren wird die wirksame Filterfläche bei gleichen Außenmaßen um bis zu 500% vergrößert, was die Standzeit verlängert.",
      fr: "Le plissage augmente la surface utile de filtration jusqu'à 500% dans un même encombrement, prolongeant la durée des cycles.",
      pt: "O pregueamento da malha aumenta a área efetiva de filtração em até 500% dentro das mesmas dimensões, prolongando a vida útil."
    }),
    card2Title: pick(lang, {
      en: "Low Initial Pressure Drop (ΔP)",
      zh: "极低初始洁净流体差压 (ΔP)",
      es: "Baja pérdida de carga inicial (ΔP)",
      de: "Niedriger Anfangsdruckverlust (ΔP)",
      fr: "Faible perte de charge initiale (ΔP)",
      pt: "Baixa perda de carga inicial (ΔP)"
    }),
    card2Desc: pick(lang, {
      en: "Optimized pleat height and pitch geometry maintain lower face velocity, reducing pump energy requirements and improving cake loading uniformity.",
      zh: "优化的折高与折距几何比例有效降低截面流速，减少泵送能耗并提升滤饼截留分布均匀度。",
      es: "La altura y el paso de pliegue optimizados reducen la velocidad frontal, disminuyendo el consumo de bombeo.",
      de: "Optimierte Faltenhöhe und -teilung senken die Anströmgeschwindigkeit und den Energiebedarf von Pumpen.",
      fr: "La géométrie optimisée de la hauteur et du pas de pli réduit la vitesse frontale et l'énergie de pompage.",
      pt: "A altura e o passo das pregas otimizados reduzem a velocidade superficial, economizando energia de bombeamento."
    }),
    card3Title: pick(lang, {
      en: "High Thermal & Chemical Resistance",
      zh: "优异耐温耐腐蚀化学相容性",
      es: "Alta resistencia térmica y química",
      de: "Hohe thermische & chemische Beständigkeit",
      fr: "Haute résistance thermique et chimique",
      pt: "Alta resistência térmica e química"
    }),
    card3Desc: pick(lang, {
      en: "Constructed entirely from AISI 316L, 304, or Hastelloy wire meshes, providing corrosion immunity in polymer extrusion and aggressive chemical refining.",
      zh: "采用 AISI 316L、304 或哈氏合金丝网制造，在聚合物挤出及强腐蚀性化工介质中保持稳定抗腐蚀性能。",
      es: "Fabricado íntegramente en acero AISI 316L, 304 o Hastelloy, resistente a la corrosión en extrusión de polímeros y refino químico.",
      de: "Vollständig aus AISI 316L, 304 oder Hastelloy gefertigt, beständig in Polymerextrusion und chemischer Raffination.",
      fr: "Fabriqué entièrement en toiles AISI 316L, 304 ou Hastelloy, résistant à la corrosion dans l'extrusion de polymères et le raffinage.",
      pt: "Construído inteiramente em telas AISI 316L, 304 ou Hastelloy, proporcionando resistência à corrosão em polímeros e refino químico."
    }),
    tableSectionTitle: pick(lang, {
      en: "Filter Media Structural Comparison",
      zh: "过滤介质结构特性对比",
      es: "Comparativa estructural de medios filtrantes",
      de: "Struktureller Vergleich von Filtermedien",
      fr: "Comparaison structurelle des médias filtrants",
      pt: "Comparativo estrutural de meios filtrantes"
    }),
    tableSectionDesc: pick(lang, {
      en: "Comparative technical characteristics of standard industrial filtration substrates.",
      zh: "标准工业流体过滤底材的综合物性与应用对比。",
      es: "Características técnicas comparativas de sustratos de filtración industrial estándar.",
      de: "Vergleichende technische Eigenschaften gängiger Filtrationssubstrate.",
      fr: "Caractéristiques techniques comparatives des substrats de filtration industrielle standard.",
      pt: "Características técnicas comparativas de substratos de filtração industrial padrão."
    }),
    badgeStandard: pick(lang, {
      en: "AISI 316L Standard",
      zh: "常用 AISI 316L",
      es: "Estándar AISI 316L",
      de: "AISI 316L Standard",
      fr: "Standard AISI 316L",
      pt: "Padrão AISI 316L"
    }),
    thMediaType: pick(lang, {
      en: "Media Type",
      zh: "介质类型",
      es: "Tipo de medio",
      de: "Medientyp",
      fr: "Type de média",
      pt: "Tipo de meio"
    }),
    thClassification: pick(lang, {
      en: "Classification",
      zh: "结构分类",
      es: "Clasificación",
      de: "Klassifizierung",
      fr: "Classification",
      pt: "Classificação"
    }),
    thMicronRange: pick(lang, {
      en: "Micron Range",
      zh: "过滤精度范围",
      es: "Rango en micras",
      de: "Mikronbereich",
      fr: "Plage en microns",
      pt: "Faixa em mícrons"
    }),
    thFlowRes: pick(lang, {
      en: "Flow Resistance",
      zh: "流动阻力",
      es: "Resistencia al flujo",
      de: "Strömungswiderstand",
      fr: "Résistance d'écoulement",
      pt: "Resistência ao fluxo"
    }),
    thApplication: pick(lang, {
      en: "Primary Application",
      zh: "主要应用场景",
      es: "Aplicación principal",
      de: "Hauptanwendung",
      fr: "Application principale",
      pt: "Aplicação principal"
    }),
    focusLabel: pick(lang, {
      en: "Selected Specification Focus",
      zh: "当前选中介质规格",
      es: "Especificación seleccionada",
      de: "Ausgewählte Spezifikation",
      fr: "Spécification sélectionnée",
      pt: "Especificação selecionada"
    }),
    cleanabilityLabel: pick(lang, {
      en: "Cleanability:",
      zh: "清洗再生方式：",
      es: "Limpiabilidad:",
      de: "Reinigbarkeit:",
      fr: "Nettoyabilité :",
      pt: "Limpabilidade:"
    }),
    rigidityLabel: pick(lang, {
      en: "Mechanical Rigidity:",
      zh: "机械刚度：",
      es: "Rigidez mecánica:",
      de: "Mechanische Steifigkeit:",
      fr: "Rigidité mécanique :",
      pt: "Rigidez mecânica:"
    }),
    viewDatasheetsBtn: pick(lang, {
      en: "View Media Technical Datasheets",
      zh: "查看介质技术数据表",
      es: "Ver fichas técnicas de medios",
      de: "Technische Datenblätter anzeigen",
      fr: "Consulter les fiches techniques",
      pt: "Ver fichas técnicas dos meios"
    }),
    bannerBadge: pick(lang, {
      en: "Custom Mesh Engineering Service",
      zh: "非标过滤滤芯工程定制",
      es: "Servicio de ingeniería de mallas a medida",
      de: "Kundenspezifischer Gewebe-Ingenieurservice",
      fr: "Service d'ingénierie de toiles sur mesure",
      pt: "Serviço de engenharia de telas sob medida"
    }),
    bannerTitle: pick(lang, {
      en: "Engineered Pleated Mesh Filter Elements",
      zh: "定制化折叠金属滤网过滤元件",
      es: "Elementos filtrantes de malla plisada diseñados a medida",
      de: "Kundenspezifische plissierte Drahtgewebe-Filterelemente",
      fr: "Éléments filtrants plissés en toile métallique sur mesure",
      pt: "Elementos filtrantes pregueados em malha metálica sob medida"
    }),
    bannerLead: pick(lang, {
      en: "Looking for OEM replacement candles, customized pleated wire cloth cylinders, or high-pressure filter packs? Our engineering team designs custom mesh pleat spacing, support layers, end caps, and inner cores to match your exact fluid specifications.",
      zh: "正在寻找 OEM 替代滤芯、定制折叠金属滤筒或高压滤材包？我们的工程团队可定制折距、支撑层、端盖与内芯骨架，精准契合您的流体工艺参数。",
      es: "¿Busca cartuchos de repuesto OEM, cilindros plisados a medida o paquetes filtrantes de alta presión? Nuestro equipo técnico diseña la separación de pliegues, capas de soporte y tapas según sus especificaciones.",
      de: "Suchen Sie OEM-Ersatzfilterkerzen, plissierte Zylinder oder Hochdruckfilterpakete? Unser Team konzipiert Faltenabstand, Stützlagen, Endkappen und Innenkerne passend zu Ihrem Fluid.",
      fr: "Vous recherchez des bougies de rechange OEM, cylindres plissés sur mesure ou packs de filtration haute pression ? Notre équipe conçoit le plissage, les couches de support et les embouts adaptés à votre fluide.",
      pt: "Procura velas de reposição OEM, cilindros pregueados sob medida ou pacotes de filtragem de alta pressão? Nossa equipe projeta o pregueamento, camadas de suporte e tampas para seu fluido."
    }),
    bannerPoint1: pick(lang, {
      en: "Custom diameters up to 500 mm",
      zh: "支持外径最大达 500 mm",
      es: "Diámetros a medida hasta 500 mm",
      de: "Kundenspezifische Durchmesser bis 500 mm",
      fr: "Diamètres sur mesure jusqu'à 500 mm",
      pt: "Diâmetros sob medida até 500 mm"
    }),
    bannerPoint2: pick(lang, {
      en: "Multi-layer support grid option",
      zh: "支持多层内/外支撑网格配置",
      es: "Opción de malla de soporte multicapa",
      de: "Optionale mehrlagige Stützgitter",
      fr: "Option de grille de support multicouche",
      pt: "Opção de tela de suporte multicamadas"
    }),
    bannerPoint3: pick(lang, {
      en: "Micron absolute ratings from 1µm",
      zh: "绝对过滤精度自 1 µm 起",
      es: "Retención absoluta desde 1 µm",
      de: "Absolute Mikronwerte ab 1 µm",
      fr: "Seuil absolu à partir de 1 µm",
      pt: "Retenção absoluta a partir de 1 µm"
    }),
    bannerPoint4: pick(lang, {
      en: "Material Certificate 3.1 available when specified in the order",
      zh: "可按订单要求提供 3.1 材质证书",
      es: "Certificado 3.1 disponible cuando se especifique en el pedido",
      de: "Zeugnis 3.1 verfügbar, sofern im Auftrag spezifiziert",
      fr: "Certificat 3.1 disponible sur demande à la commande",
      pt: "Certificado 3.1 disponível quando especificado no pedido"
    }),
    bannerReqBtn: pick(lang, {
      en: "Request pleated mesh quote",
      zh: "索取折叠滤网报价",
      es: "Solicitar cotización de malla plisada",
      de: "Angebot für Plissiergewebe anfordern",
      fr: "Demander un devis pour toile plissée",
      pt: "Solicitar cotação de malha pregueada"
    }),
    bannerOemBtn: pick(lang, {
      en: "Explore OEM Engineering",
      zh: "了解 OEM 定制工程",
      es: "Explorar ingeniería OEM",
      de: "OEM-Technik entdecken",
      fr: "Découvrir l'ingénierie OEM",
      pt: "Explorar engenharia OEM"
    }),
    imgCaptionTitle: pick(lang, {
      en: "316L Pleated Element",
      zh: "316L 折叠滤芯元件",
      es: "Elemento plisado 316L",
      de: "316L Plissiertes Element",
      fr: "Élément plissé 316L",
      pt: "Elemento pregueado 316L"
    }),
    imgCaptionSub: pick(lang, {
      en: "Pleat Depth: 12.5mm | 10µm Absolute",
      zh: "折深：12.5mm | 10µm 绝对精度",
      es: "Profundidad de pliegue: 12.5mm | 10µm absoluto",
      de: "Faltentiefe: 12,5mm | 10µm absolut",
      fr: "Profondeur de pli : 12,5mm | 10µm absolu",
      pt: "Profundidade da prega: 12,5mm | 10µm absoluto"
    }),
    imgAlt: pick(lang, {
      en: "Pleated stainless steel woven wire mesh filter element detail",
      zh: "折叠不锈钢编织丝网滤芯元件细节",
      es: "Detalle de elemento filtrante de malla de alambre tejida plisada de acero inoxidable",
      de: "Detail eines plissierten Edelstahl-Drahtgewebe-Filterelements",
      fr: "Détail d'un élément filtrant plissé en toile métallique d'acier inoxydable",
      pt: "Detalhe do elemento filtrante pregueado em malha de arame de aço inoxidável"
    })
  };
  return <section id="filtermedia" data-nav="light" className="bg-muted/50 text-foreground mf-section">
      <div className="mf-container">
        
        {/* Section Header */}
        <Reveal>
          <div className="max-w-[65ch] mb-12">
            <Badge variant="outline" className="bg-muted text-foreground mf-caption font-mono uppercase tracking-wider mb-4 border border-border">
              {copy.badge}
            </Badge>
            <h2 className="mf-h2 text-foreground mb-4">
              {copy.h2}
            </h2>
            <p className="text-muted-foreground mf-lead">
              {copy.lead}
            </p>
          </div>
        </Reveal>

        {/* Pleated Mesh Highlight Cards / Features */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mb-12">
          <Card className="border border-border bg-card">
            <CardHeader className="pb-3">
              <div className="inline-flex size-10 items-center justify-center rounded-md border border-border bg-muted text-primary mb-2">
                <SlidersHorizontal className="size-5" aria-hidden="true" />
              </div>
              <CardTitle className="text-lg font-semibold">
                {copy.card1Title}
              </CardTitle>
            </CardHeader>
            <CardContent>
              <CardDescription className="text-muted-foreground mf-caption leading-relaxed">
                {copy.card1Desc}
              </CardDescription>
            </CardContent>
          </Card>

          <Card className="border border-border bg-card">
            <CardHeader className="pb-3">
              <div className="inline-flex size-10 items-center justify-center rounded-md border border-border bg-muted text-primary mb-2">
                <Gauge className="size-5" aria-hidden="true" />
              </div>
              <CardTitle className="text-lg font-semibold">
                {copy.card2Title}
              </CardTitle>
            </CardHeader>
            <CardContent>
              <CardDescription className="text-muted-foreground mf-caption leading-relaxed">
                {copy.card2Desc}
              </CardDescription>
            </CardContent>
          </Card>

          <Card className="border border-border bg-card">
            <CardHeader className="pb-3">
              <div className="inline-flex size-10 items-center justify-center rounded-md border border-border bg-muted text-primary mb-2">
                <ShieldCheck className="size-5" aria-hidden="true" />
              </div>
              <CardTitle className="text-lg font-semibold">
                {copy.card3Title}
              </CardTitle>
            </CardHeader>
            <CardContent>
              <CardDescription className="text-muted-foreground mf-caption leading-relaxed">
                {copy.card3Desc}
              </CardDescription>
            </CardContent>
          </Card>
        </div>

        {/* Technical Media Comparison Table Section */}
        <div className="bg-card border border-border rounded-lg p-6 mb-12">
          <div className="flex flex-col md:flex-row md:items-center justify-between gap-4 mb-6">
            <div>
              <h3 className="mf-h3 text-foreground">
                {copy.tableSectionTitle}
              </h3>
              <p className="text-muted-foreground mf-caption">
                {copy.tableSectionDesc}
              </p>
            </div>
            <div className="flex items-center gap-2">
              <Badge variant="outline" className="mf-caption">
                {copy.badgeStandard}
              </Badge>
            </div>
          </div>

          <div className="overflow-x-auto">
            <Table>
              <TableHeader>
                <TableRow className="bg-muted/40">
                  <TableHead className="font-semibold text-foreground text-sm">{copy.thMediaType}</TableHead>
                  <TableHead className="font-semibold text-foreground text-sm">{copy.thClassification}</TableHead>
                  <TableHead className="font-semibold text-foreground text-sm">{copy.thMicronRange}</TableHead>
                  <TableHead className="font-semibold text-foreground text-sm">{copy.thFlowRes}</TableHead>
                  <TableHead className="font-semibold text-foreground text-sm">{copy.thApplication}</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {mediaSpecs.map((media, i) => <TableRow key={media.id} data-index={i} className={`cursor-pointer transition-colors ${selectedMediaId === media.id ? "bg-accent/20 font-medium" : ""}`} onClick={() => setSelectedMediaId(media.id)}>
                    <TableCell className="font-medium text-foreground flex items-center gap-2 text-sm">
                      <Filter className="size-4 text-primary shrink-0" />
                      {media.name}
                    </TableCell>
                    <TableCell className="text-muted-foreground text-sm">{media.category}</TableCell>
                    <TableCell className="font-mono text-sm">{media.micronRating}</TableCell>
                    <TableCell className="text-muted-foreground text-sm">{media.flowResistance}</TableCell>
                    <TableCell className="text-muted-foreground text-sm">
                      {media.primaryApplication}
                    </TableCell>
                  </TableRow>)}
              </TableBody>
            </Table>
          </div>

          {/* Detailed Selected Media Card */}
          <div className="mt-6 p-4 rounded-lg bg-muted/40 border border-border flex flex-col md:flex-row items-start md:items-center justify-between gap-4">
            <div className="space-y-1">
              <div className="flex items-center gap-2">
                <span className="mf-caption font-semibold uppercase tracking-wide text-primary">
                  {copy.focusLabel}
                </span>
                <span className="text-muted-foreground mf-caption">•</span>
                <span className="text-sm font-semibold text-foreground">{selectedMedia.name}</span>
              </div>
              <p className="mf-caption text-muted-foreground">
                <strong className="text-foreground">{copy.cleanabilityLabel}</strong> {selectedMedia.cleanability} |{" "}
                <strong className="text-foreground">{copy.rigidityLabel}</strong> {selectedMedia.rigidity}
              </p>
            </div>
            <Button asChild size="sm" variant="outline" className="shrink-0 gap-1.5">
              <Link to="/TechnicalResourcesPage">
                <FileText className="size-3.5" />
                {copy.viewDatasheetsBtn}
              </Link>
            </Button>
          </div>
        </div>

        {/* Pleated Mesh Featured Banner & CTA Box */}
        <Reveal>
          <div className="relative overflow-hidden rounded-lg bg-surface-dark text-surface-dark-foreground border border-surface-dark-border p-8 md:p-12">
            <div className="grid grid-cols-1 lg:grid-cols-12 gap-8 items-center relative z-10">
              
              <div className="lg:col-span-7 space-y-4">
                <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full bg-surface-dark-elevated border border-surface-dark-border mf-caption font-medium text-surface-dark-foreground">
                  <Zap className="size-3.5 text-accent" />
                  {copy.bannerBadge}
                </div>
                <h3 className="mf-h3 font-semibold text-surface-dark-foreground">
                  {copy.bannerTitle}
                </h3>
                <p className="text-surface-dark-muted mf-lead">
                  {copy.bannerLead}
                </p>
                
                <ul className="grid grid-cols-1 sm:grid-cols-2 gap-2 pt-2 mf-caption text-surface-dark-foreground">
                  <li className="flex items-center gap-2">
                    <CircleCheck className="size-4 text-accent shrink-0" aria-hidden="true" />
                    <span>{copy.bannerPoint1}</span>
                  </li>
                  <li className="flex items-center gap-2">
                    <CircleCheck className="size-4 text-accent shrink-0" aria-hidden="true" />
                    <span>{copy.bannerPoint2}</span>
                  </li>
                  <li className="flex items-center gap-2">
                    <CircleCheck className="size-4 text-accent shrink-0" aria-hidden="true" />
                    <span>{copy.bannerPoint3}</span>
                  </li>
                  <li className="flex items-center gap-2">
                    <CircleCheck className="size-4 text-accent shrink-0" aria-hidden="true" />
                    <span>{copy.bannerPoint4}</span>
                  </li>
                </ul>

                {/* CTAs */}
                <div className="pt-4 flex flex-wrap items-center gap-4">
                  <Button asChild size="lg">
                    <Link to="/RequestAQuotePage">
                      {copy.bannerReqBtn}
                      <ArrowRight className="size-4 ml-1" />
                    </Link>
                  </Button>
                  <Button asChild variant="outline" size="lg" className="border-surface-dark-border text-surface-dark-foreground bg-surface-dark-elevated hover:bg-surface-dark">
                    <Link to="/OEMCustomMeshEngineeringPage">
                      {copy.bannerOemBtn}
                    </Link>
                  </Button>
                </div>
              </div>

              {/* Illustrative Product Image Panel */}
              <div className="lg:col-span-5 flex justify-center lg:justify-end">
                <div className="relative mf-media-framed w-full max-w-md mf-ratio-card">
                  <img src="https://wpvc-images.s3.us-east-1.amazonaws.com/images/1775455/img/pleated_filter_mesh.png" data-wvc-srcset="https://wpvc-images.s3.us-east-1.amazonaws.com/images/1775455/img/pleated_filter_mesh.png-wvc-srcset" data-wvc-sizes="https://wpvc-images.s3.us-east-1.amazonaws.com/images/1775455/img/pleated_filter_mesh.png-wvc-sizes" alt={copy.imgAlt} className="w-full h-full object-cover" loading="lazy" decoding="async" />
                  <div className="absolute bottom-3 left-3 right-3 bg-card border border-border rounded p-2.5">
                    <p className="font-semibold text-sm text-foreground">{copy.imgCaptionTitle}</p>
                    <p className="mf-caption text-muted-foreground">{copy.imgCaptionSub}</p>
                  </div>
                </div>
              </div>

            </div>
          </div>
        </Reveal>

      </div>
    </section>;
}